/**
 * ใบสั่งผลิต (Production Order) — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Production Order — Payload Contract")
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง ฝั่ง API
 * (`WO-P1-M5-001`) กำลังถูกสร้างขนานกันจากเอกสารฉบับเดียวกัน — ถ้าสองฝั่งไม่ตรงกัน
 * ให้แก้เอกสารก่อน ห้ามฝ่ายใดฝ่ายหนึ่งขยับเอง
 *
 * **สามฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของและ client ส่งไม่ได้เลย** — `order_no`,
 * `status` และ `source_type` ชนิดข้อมูลของ body ในไฟล์นี้จึงไม่มีช่องให้เขียนทั้งสาม
 * ค่า: เลขที่ client เลือกเองคือสอง client แข่งกันชน unique, สถานะที่ client ส่งเอง
 * ทำให้ state machine ข้อ 14 ไร้ความหมาย และ `source_type` ที่ client อ้างเองคือการ
 * ปลอมว่า order นี้มาจากการวางแผน (D-034)
 *
 * ⚠️ `planned_quantity` เป็น **string** ทั้งขาเข้าและขาออก เพราะ `NUMERIC` เก็บใน
 * IEEE-754 double ไม่ครบ ห้ามแปลงผ่าน `Number`/`parseFloat` ไม่ว่าจะเพื่อแสดงผล
 * หรือเพื่อส่งกลับ — จำนวนที่เพี้ยนไปหนึ่งทศนิยมคือใบสั่งผลิตที่ผลิตของผิดจำนวน
 */

/** รหัสโมดูลของกลุ่มนี้ใน RBAC matrix — ค่าเดียวกับที่ contract ใช้กับทุก endpoint */
export const PRODUCTION_ORDER_MODULE = 'PRODUCTION_ORDER';

/* --------------------------------------------------------- state machine -- */

/**
 * สถานะทั้งหมดของ `production_order` ตาม `BUSINESS_RULES.md` ข้อ 14
 *
 * `DRAFT` → `RELEASED` → `IN_PROCESS` → `COMPLETED` → `CLOSED` และ
 * `DRAFT`/`RELEASED` → `CANCELLED` — `CLOSED`/`CANCELLED` เป็นสถานะสุดท้าย
 */
export const PRODUCTION_ORDER_STATUSES = [
  'DRAFT',
  'RELEASED',
  'IN_PROCESS',
  'COMPLETED',
  'CLOSED',
  'CANCELLED',
] as const;

export type ProductionOrderStatus = (typeof PRODUCTION_ORDER_STATUSES)[number];

/**
 * สถานะที่ `release` รับได้ — มีค่าเดียว และเป็น `expected_status` ที่จะถูกส่งไป
 *
 * ประกาศไว้เป็นค่าคงที่เพื่อให้หน้าจอ เทสต์ และข้อความบนจอ อ้างสตริงชุดเดียวกัน
 * ⚠️ แต่ค่าที่ **ส่งจริง** ต้องอ่านจาก order ที่กำลังถูกแสดงอยู่ ไม่ใช่ค่าคงที่นี้
 * (ดู `ProductionOrderReleasePanel`) — guard ที่ส่งค่าที่ตัวเองเขียนไว้ล่วงหน้าไม่ได้
 * ยืนยันอะไรเลย
 */
export const RELEASABLE_STATUS: ProductionOrderStatus = 'DRAFT';

/**
 * `source_type` เดียวที่ Phase 1 มี — เซิร์ฟเวอร์บังคับเอง client ส่งมาไม่ได้
 *
 * `MPS`/`MRP`/`KANBAN` สงวนไว้ให้ source adapter ในอนาคต (D-034) หน้าจอนี้จึงไม่มี
 * ตัวเลือก `source_type` ให้ผู้ใช้เลย ไม่ใช่แค่ตั้งค่าเริ่มต้นไว้ที่ `MANUAL`
 */
export const MANUAL_SOURCE_TYPE = 'MANUAL';

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `422` ตอน `release` เมื่อ product+plant นั้นยังไม่มี BOM ที่ใช้อยู่
 *
 * ⚠️ **คนละข้อความกับ `NO_ACTIVE_ROUTING` เสมอ** contract เขียนไว้ตรง ๆ ว่า
 * "บอกว่าขาดอะไร ไม่ใช่ข้อความรวม เพราะวิศวกรต้องรู้ว่าต้องไปสร้างอะไรต่อ"
 */
export const NO_ACTIVE_BOM = 'NO_ACTIVE_BOM';

/** `422` ตอน `release` เมื่อยังไม่มี Routing ที่ใช้อยู่ — ดู `NO_ACTIVE_BOM` */
export const NO_ACTIVE_ROUTING = 'NO_ACTIVE_ROUTING';

/** `422` เมื่อ client ส่ง `source_type` ที่สงวนไว้ให้ adapter มาเอง (D-034) */
export const SOURCE_TYPE_RESERVED_FOR_ADAPTER = 'SOURCE_TYPE_RESERVED_FOR_ADAPTER';

/** `422` เมื่อ product ยังไม่ `ACTIVE` หรือไม่มี `product_plant_data` ของ Plant นั้น */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';

/**
 * `422` ตอน `release` เมื่อผู้สั่งปล่อยเป็นคนเดียวกับผู้สร้าง order (D-010)
 *
 * ⚠️ **ความหมายที่สามของรหัสเดียวกัน** — ของ product คือผู้สร้างสินค้า, ของคำขอ
 * เปลี่ยนแปลงคือผู้ยื่นคำขอ, ที่นี่คือ **ผู้เปิดใบสั่งผลิตใบนี้**
 * (`released_by_employee_id` ห้ามเท่ากับ `created_by_employee_id` ของ order นั้น)
 * การใช้ข้อความเดียวกันทั้งสามที่จะทำให้ผู้อนุมัติไปตามหาคนผิดคน
 */
export const APPROVER_MUST_DIFFER_FROM_CREATOR = 'APPROVER_MUST_DIFFER_FROM_CREATOR';

/**
 * กฎทุกข้อที่ contract เขียนไว้ตรง ๆ สำหรับกลุ่มนี้ — ประกาศเป็นรายการเพื่อให้
 * หน้าจอ เทสต์ และข้อความบนจอ อ้างสตริงชุดเดียวกัน ไม่มีวันเลื่อนออกจากกัน
 */
export const PRODUCTION_ORDER_RULES = [
  NO_ACTIVE_BOM,
  NO_ACTIVE_ROUTING,
  SOURCE_TYPE_RESERVED_FOR_ADAPTER,
  PRODUCT_NOT_ACTIVE,
  APPROVER_MUST_DIFFER_FROM_CREATOR,
] as const;

export type ProductionOrderRule = (typeof PRODUCTION_ORDER_RULES)[number];

/* --------------------------------------------------------------- resource -- */

/**
 * หนึ่งแถวของ `production_order_operation` — snapshot ของ `routing_operation`
 * ณ เวลา release (`docs/ERD.md`)
 *
 * ⚠️ contract freeze **body ของ `POST`** ไว้ครบ แต่ไม่ได้ freeze รูปร่างของคำตอบ
 * `GET` ชนิดข้อมูลนี้จึงบังคับเฉพาะ `operation_seq` ซึ่งเป็น PK ร่วมและเป็นสิ่งที่
 * หน้าจอต้องใช้เรียงลำดับ ส่วนที่เหลืออ่านได้แต่ไม่บังคับ — เดาให้แคบกว่าที่เอกสาร
 * เขียนไว้ แล้วปฏิเสธคำตอบที่ถูกต้อง คือความเสียหายที่แย่กว่าการอ่านได้หลายรูปแบบ
 * (แนวเดียวกับ `lib/bom/types.ts` และรายงานเป็น blocker แล้ว)
 */
export interface ProductionOrderOperation {
  readonly operation_seq: number;
  readonly work_center_code: string | null;
  readonly tool_code: string | null;
  /** `NUMERIC` — string เสมอ */
  readonly std_time: string | null;
  /** `NUMERIC` — string เสมอ */
  readonly setup_time: string | null;
}

/**
 * ใบสั่งผลิตหนึ่งใบตามที่เซิร์ฟเวอร์ส่งกลับมา
 *
 * บังคับสองฟิลด์เท่านั้น: `production_order_id` (ใช้ประกอบ URL ของ detail/release
 * จึงขาดไม่ได้) และ `status` (ทั้งหน้าจอตัดสินใจจากมัน และเป็น `expected_status`
 * ที่จะถูกส่งกลับไป) ที่เหลือ nullable ด้วยเหตุผลเดียวกับ
 * `ProductionOrderOperation`
 *
 * `bom_id`/`routing_id` เป็น `null` จนกว่าจะ release — นั่นคือ **สาระสำคัญทั้งหมด**
 * ของข้อ 2 ใน `BUSINESS_RULES.md` ไม่ใช่ฟิลด์ที่ยังไม่ได้กรอก
 */
export interface ProductionOrder {
  readonly production_order_id: string;
  /** **เซิร์ฟเวอร์เป็นผู้กำหนด** — client ส่งมาไม่ได้และผู้ใช้พิมพ์เองไม่ได้ */
  readonly order_no: string | null;
  readonly plant_code: string | null;
  readonly product_code: string | null;
  /** `NUMERIC` — string เสมอ ห้ามแปลงเป็น number */
  readonly planned_quantity: string | null;
  readonly planned_start_date: string | null;
  readonly planned_end_date: string | null;
  /** **เซิร์ฟเวอร์เป็นเจ้าของ** — ทุกการเปลี่ยนสถานะผ่าน endpoint ของมันเท่านั้น */
  readonly status: string;
  /** บังคับเป็น `MANUAL` ใน Phase 1 (D-034) — หน้าจอไม่มีตัวเลือกให้ */
  readonly source_type: string | null;
  readonly source_reference_id: string | null;
  /** snapshot ตอน release — `null` แปลว่ายังไม่ได้สั่งปล่อย ไม่ใช่ "ยังไม่ได้กรอก" */
  readonly bom_id: string | null;
  readonly routing_id: string | null;
  readonly created_by_employee_id: string | null;
  /** ต้องไม่ใช่คนเดียวกับ `created_by_employee_id` (D-010) */
  readonly released_by_employee_id: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  /** snapshot ของ routing ที่ถูก copy เข้ามาตอน release — ว่างก่อนสั่งปล่อย */
  readonly operations: readonly ProductionOrderOperation[];
}

export interface ProductionOrderListPage {
  readonly items: readonly ProductionOrder[];
  readonly next_cursor: string | null;
}

/**
 * Body ของ `POST /v1/production-orders` — ห้าคีย์ ไม่มีคีย์ที่หก
 *
 * **ไม่มี `status`, `order_no`, `source_type` และ `source_reference_id`** โดย
 * ชนิดข้อมูล ไม่ใช่โดยความตั้งใจของโค้ดที่ประกอบ request — ฟิลด์ที่ไม่มีที่ให้เขียน
 * คือฟิลด์ที่หลุดออกไปไม่ได้แม้วันที่มีคนเพิ่มช่องใหม่ในฟอร์ม
 */
export interface CreateProductionOrderBody {
  /** บังคับเสมอ และเป็นขอบเขตสิทธิ์ (D-004) — นอก scope เซิร์ฟเวอร์ตอบ `403` */
  readonly plant_code: string;
  readonly product_code: string;
  /** `NUMERIC` — string เสมอ ต้องมากกว่า 0 */
  readonly planned_quantity: string;
  readonly planned_start_date: string;
  /** ต้องอยู่หลัง `planned_start_date` — ผิดเซิร์ฟเวอร์ตอบ `400` */
  readonly planned_end_date: string;
}

/** Body ของ `release` — คีย์เดียว เหมือน transition ทุกตัวใน contract */
export interface ProductionOrderTransitionBody {
  readonly expected_status: string;
}

/**
 * ตัวกรองของ `GET /v1/production-orders`
 *
 * contract ระบุว่า list "กรองตาม plant scope เสมอ และรองรับ filter ตาม `status`
 * กับ `product_code`" ส่วน `limit`/`cursor` ตามรูปแบบ pagination เดียวกับ
 * `GET /v1/products`
 */
export interface ProductionOrderListFilters {
  readonly plant_code?: string;
  readonly status?: string;
  readonly product_code?: string;
  readonly limit?: number;
  readonly cursor?: string;
}
